/**
 * useBulkSelection — tiny hook for multi-row selection.
 *
 * Powers the "Bulk Resend" action bar on DAR / Incidents / Maintenance pages.
 * A single `Set<string>` of ids drives the UI; `toggle`, `clear`, `selectAll`
 * and `isSelected` are stable references so consumers can drop them straight
 * into JSX without triggering unnecessary re-renders.
 *
 * The hook is intentionally generic — no report-kind knowledge lives here.
 */
import { useCallback, useMemo, useState } from "react";

export default function useBulkSelection() {
  const [selected, setSelected] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const selectAll = useCallback((ids) => {
    setSelected(new Set(ids));
  }, []);

  const isSelected = useCallback((id) => selected.has(id), [selected]);

  return useMemo(() => ({
    selected,
    count: selected.size,
    toggle,
    clear,
    selectAll,
    isSelected,
  }), [selected, toggle, clear, selectAll, isSelected]);
}
